// Evidence-derived relevance score (Issue #21).
//
// The deterministic-first pipeline classifies every edge as "deterministic" or
// "candidate" and records *why* it exists as an array of Evidence. Historically
// downstream consumers filtered on a single `confidence` threshold; that scalar
// conflates "how sure are we this edge is real" (telemetry) with "how relevant
// is this edge for ranking" (the question consumers actually ask).
//
// `computeRelevanceScore` derives a deterministic relevance score from the
// *kinds* of evidence backing an edge — independent of the confidence value,
// which stays as telemetry metadata. The score is the strongest single
// evidence weight (the best signal available) scaled by a diversity bonus:
// independent corroborating signals of different kinds make an edge more
// relevant than a single signal repeated.
//
// The score is deterministic: it depends only on the multiset of evidence
// types present, never on iteration order, timestamps, or RNG.

import type { Evidence, EvidenceType } from "./types.js";

// ---------------------------------------------------------------------------
// Weights
// ---------------------------------------------------------------------------

/**
 * Per-evidence-type relevance weight. Static, manifest-backed facts (weight
 * 1.0) are the strongest; LLM/heuristic signals weigh less. Weights are by
 * evidence *type*, not by the per-edge confidence value.
 */
export const RELEVANCE_WEIGHTS: Record<EvidenceType, number> = {
  static_import: 1.0,
  dynamic_import: 0.8,
  manifest_dependency: 1.0,
  path_pattern_match: 0.9,
  naming_convention: 0.6,
  matched_schema_name: 0.6,
  nearby_import: 0.5,
  tool_output_field_similarity: 0.3,
  llm_semantic_link: 0.4,
};

/** Weight applied to an evidence type absent from {@link RELEVANCE_WEIGHTS}. */
export const UNKNOWN_EVIDENCE_WEIGHT = 0;

/**
 * Diversity multipliers applied to the base weight. An edge corroborated by
 * several *distinct* evidence kinds is more relevant than one backed by a
 * single kind (even if repeated).
 */
export const DIVERSITY_BONUS = {
  /** >= 2 distinct evidence kinds. */
  two: 1.2,
  /** >= 3 distinct evidence kinds. */
  three: 1.3,
} as const;

export interface ComputeRelevanceScoreOptions {
  /**
   * Evidence types to exclude from the score (e.g. CLI --evidence-exclude).
   * Excluded evidence contributes neither weight nor diversity.
   */
  exclude?: Iterable<EvidenceType | string>;
}

/**
 * Compute a deterministic relevance score for an edge from its evidence.
 *
 * The score is `maxWeight * diversityBonus`, where `maxWeight` is the largest
 * weight among the (non-excluded) evidence types present and `diversityBonus`
 * grows with the number of *distinct* kinds. Empty (or fully-excluded) evidence
 * scores 0. Unknown evidence types contribute {@link UNKNOWN_EVIDENCE_WEIGHT}
 * but still count toward diversity.
 */
export function computeRelevanceScore(
  evidence: Evidence[],
  options: ComputeRelevanceScoreOptions = {},
): number {
  const exclude = new Set<string>(options.exclude ?? []);

  let maxWeight = 0;
  const kinds = new Set<string>();
  for (const e of evidence) {
    if (exclude.has(e.type)) continue;
    kinds.add(e.type);
    const weight = RELEVANCE_WEIGHTS[e.type] ?? UNKNOWN_EVIDENCE_WEIGHT;
    if (weight > maxWeight) maxWeight = weight;
  }

  if (kinds.size === 0) return 0;

  const diversity =
    kinds.size >= 3
      ? DIVERSITY_BONUS.three
      : kinds.size >= 2
        ? DIVERSITY_BONUS.two
        : 1;

  return maxWeight * diversity;
}
